import React, { useState, useEffect, useContext, useCallback } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
    Container,
    Row,
    Col,
    Breadcrumb,
    InputGroup,
    Button,
    Form,
} from "react-bootstrap";

import EventService from "service/eventService";
import OrganisationService from "service/organisationService";
import ContentWrapper from "components/content-wrapper/ContentWrapper";
import SelectRecipientModal from "components/modals/SelectRecipientModal";
import { isHttpSuccess, orgId } from "utils/functions";
import { AppContext } from "context/appContext";

import "assets/css/event.css";
import { showErrorAlert } from "utils/alert";
import { ActionType, CharacterLimit } from "constant";
import {
    EVENT_ACTION_NO_RECIPIENT_MESSAGE,
    NEW_ACTION_MESSAGE,
    NOT_FOUND_PHONE_NUMBER_MESSAGE,
    SMS_RECIPIENT_MESSAGE,
    UPDATE_ACTION_MESSAGE,
} from "constant/messages";
import { MemberDto, ResponseDto } from "types";
import { getMemberName } from "utils/organisationFunction";
import SMSRecipientNoVerified from "components/gateways/SMSRecipientNoVerified";
import { isEmpty, maxBy } from "lodash";
import { handleAPIResp } from "components/events/ActionDetail/handleActionApiUtil";
import { useModal } from "components/modals/ModalTemplate";

const SendSMS = () => {
    const history: any = useHistory();
    const params: any = useParams();
    const [message, setMessage] = useState("");

    const [initialMemberList, setInitialMemberList] = useState<
        Array<MemberDto>
    >([]);
    const [smsList, setSMSList] = useState<Array<MemberDto>>([]);

    const [isPageLoading, setIsPageLoading] = useState(true);

    const [selectRecipientShow, setSelectRecipientShow] = useState(false);

    const { storeData } = useContext(AppContext);
    const { isDefault, owner } = storeData.organization.currentOrgInfo;
    const { givenName, familyName, phoneNumber, phoneNumberVerified } =
        storeData.user.userData;

    const { openModal, modal } = useModal();

    const backToEventPage = useCallback(() => {
        history.push(`/event-details/${params.eventId}`);
    }, [history, params.eventId]);

    useEffect(() => {
        if (params.actionId) {
            const fetchAction = async () => {
                const response = await EventService.readOneAction(
                    params.eventId,
                    params.actionId
                );
                if (isHttpSuccess(response.status)) {
                    const { content, recipients } = response.data.resource;
                    setMessage(content);
                    const recipientListRes: any = [];
                    const members =
                        await OrganisationService.getAvailableOrgMembers(
                            orgId()
                        );

                    if (!isEmpty(members) && !isEmpty(recipients)) {
                        for (const savedRecipient of recipients) {
                            const tempRecipient = members.find((member) => {
                                return (
                                    member?.username?.toLowerCase() ===
                                    savedRecipient?.toLowerCase()
                                );
                            });
                            recipientListRes.push(
                                toCamelCaseObject(tempRecipient)
                            );
                        }

                        setInitialMemberList(members);
                    }
                    setSMSList(recipientListRes);

                    setIsPageLoading(false);
                } else {
                    setIsPageLoading(false);
                    openModal({
                        key: "action_sms_error",
                        modalType: "err",
                        modalContent: response.data.description,
                        okAction: () => {
                            backToEventPage();
                        },
                    });
                }
            };
            fetchAction();
        } else {
            fetchOrg();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.actionId, params.eventId]);

    const fetchOrg = async () => {
        const members = await OrganisationService.getAvailableOrgMembers(
            orgId()
        );

        setInitialMemberList(members);
        setIsPageLoading(false);
    };

    const getRecipients = () => {
        if (isDefault) {
            if (!phoneNumberVerified) {
                showErrorAlert({
                    message: EVENT_ACTION_NO_RECIPIENT_MESSAGE,
                });
                return { recipients: [], error: true };
            }
            return { recipients: [owner], error: false };
        } else {
            if (smsList.length === 0) {
                showErrorAlert({
                    message: EVENT_ACTION_NO_RECIPIENT_MESSAGE,
                });
                return { recipients: [], error: true };
            }
            return {
                recipients: smsList.map((member: MemberDto) => member.username),
                error: false,
            };
        }
    };

    const transformPayload = async (recipients: string[]) => {
        const payload = {
            type_: ActionType.SMS,
            resource: {
                recipients,
                content: message.trim(),
            },
        };

        if (!params.actionId) {
            const { data } = await EventService.readOneEvent(params.eventId);
            const { actions = [] } = data;

            const latestAction: any = maxBy(actions, "order");
            const max = latestAction?.order ?? 0;

            return {
                ...payload,
                order: max + 1,
            };
        }

        return payload;
    };

    const handleButtonAction = async () => {
        if (
            !message.trim().length ||
            message.trim().length > CharacterLimit.EVENT_SMS_MESSAGE_MAX
        ) {
            showErrorAlert({
                message: `Message field is required and cannot exceed ${CharacterLimit.EVENT_SMS_MESSAGE_MAX} characters in length.`,
            });
            return;
        }

        const { recipients, error } = getRecipients();
        if (error) return;

        const payload = await transformPayload(recipients);

        let response: ResponseDto<any>;

        const { actionId, eventId } = params;

        if (actionId) {
            response = await EventService.updateAction(
                eventId,
                actionId,
                payload
            );
        } else {
            response = await EventService.createAction(eventId, payload);
        }

        const successMessage = actionId
            ? UPDATE_ACTION_MESSAGE
            : NEW_ACTION_MESSAGE;

        handleAPIResp({
            response,
            successMessage,
            defaultErrorMessage: "Unable to save the SMS action",
            backToEventPage,
        });
    };

    const deleteSMSFromList = (email: string) => {
        const clone = [...smsList];
        setSMSList(clone.filter((item) => item.username !== email));
    };

    const renderSMSRecipient = () => {
        if (isDefault) {
            if (phoneNumberVerified) {
                return (
                    <div className="added-item recipients-added">
                        <p className="mb-0 mr-4 added-item-recipient">{`${givenName} ${familyName}`}</p>{" "}
                        <h6 className="ginfo">{phoneNumber}</h6>
                    </div>
                );
            }

            return <SMSRecipientNoVerified />;
        }

        if (smsList) {
            const lst = smsList.map((i) => {
                return (
                    <React.Fragment key={i.uuid}>
                        <div className="added-item recipients-added">
                            <p className="mb-0 added-item-recipient">
                                {getMemberName(i)}
                            </p>{" "}
                            <h6 className="ginfo">{i.username}</h6>
                            <span
                                className="material-icons recipient-close-btn"
                                onClick={() => deleteSMSFromList(i.username)}
                                onKeyDown={() => deleteSMSFromList(i.username)}
                            >
                                close
                            </span>
                            {!i.phoneNumberVerified && (
                                <span className="d-flex align-items-center ml-0">
                                    <i className="material-icons">
                                        error_outline
                                    </i>
                                    <span className="phone-number-error-font-size">
                                        {NOT_FOUND_PHONE_NUMBER_MESSAGE}
                                    </span>
                                </span>
                            )}
                        </div>
                    </React.Fragment>
                );
            });
            return lst;
        }
    };

    return (
        <ContentWrapper isLoading={isPageLoading}>
            <Container>
                <Row>
                    <Col sm="12" className="event-detail-head">
                        <h5 className="page-title overflow-text">Send SMS</h5>
                    </Col>
                </Row>
                <Row>
                    <Col sm="12">
                        <Breadcrumb className="w-100">
                            <Breadcrumb.Item href="/events">
                                Events
                            </Breadcrumb.Item>
                            <Breadcrumb.Item
                                href={`/event-details/${params.eventId}`}
                            >
                                Event Details
                            </Breadcrumb.Item>
                            <Breadcrumb.Item active>Action</Breadcrumb.Item>
                        </Breadcrumb>
                    </Col>
                </Row>
                <Row className="condition-detail-box">
                    <Col>
                        <div className="form-box mb-3">
                            <Form.Label>Message</Form.Label>
                            <Form.Control
                                as="textarea"
                                placeholder="Enter Message Here"
                                rows={3}
                                value={message}
                                onChange={(e: any) => {
                                    setMessage(e.target.value);
                                }}
                            />
                        </div>
                        <div className="form-box mb-3">
                            <h5 className="mb-4">Recipients</h5>
                            <Form.Row>
                                <Col md="5" sm="12">
                                    <InputGroup>
                                        <Button
                                            variant="primary"
                                            className="ml-1"
                                            hidden={isDefault}
                                            onClick={() => {
                                                setSelectRecipientShow(true);
                                            }}
                                        >
                                            + Add
                                        </Button>
                                    </InputGroup>
                                </Col>
                            </Form.Row>
                            <Form.Row>
                                <div className="recipients sms">
                                    <Row>
                                        <Col className="mt-3 ml-2">
                                            {renderSMSRecipient()}
                                        </Col>
                                    </Row>
                                    {!isDefault && (
                                        <Row className="recipient-msg">
                                            <Col className="mt-12 ml-2 recipientFooter">
                                                {SMS_RECIPIENT_MESSAGE}
                                            </Col>
                                        </Row>
                                    )}
                                </div>
                            </Form.Row>
                        </div>
                        <Button
                            variant="secondary"
                            className="mr-2"
                            onClick={backToEventPage}
                        >
                            CANCEL
                        </Button>
                        <Button
                            variant="primary"
                            className="pl-4 pr-4"
                            onClick={handleButtonAction}
                        >
                            {params.actionId ? "UPDATE" : "ADD"}
                        </Button>
                    </Col>
                </Row>
            </Container>
            {/* Select Recipient Modal */}
            <SelectRecipientModal
                show={selectRecipientShow}
                recipientMode={"SMS"}
                smsList={smsList}
                setSMSList={setSMSList}
                onClose={() => setSelectRecipientShow(false)}
                initialMemberList={initialMemberList}
            />

            {modal}
        </ContentWrapper>
    );
};

export default SendSMS;
